import { useEffect, useRef, useState, type ReactNode } from 'react';

import { IconChevron } from '../layout/icons';

interface CkEditorInstance {
  getData: () => string;
  setData: (value: string) => void;
  destroy: () => Promise<void>;
  enableReadOnlyMode: (lockId: string) => void;
  disableReadOnlyMode: (lockId: string) => void;
  model: { document: { on: (event: 'change:data', callback: () => void) => void } };
}

interface CkEditorBuild {
  ClassicEditor: {
    create: (element: HTMLElement, config: Record<string, unknown>) => Promise<CkEditorInstance>;
  };
}

declare global {
  interface Window {
    CKEDITOR?: CkEditorBuild;
  }
}

const ckEditorScriptUrl = 'https://cdn.ckeditor.com/ckeditor5/41.4.2/super-build/ckeditor.js';
let ckEditorLoader: Promise<void> | null = null;

const ckEditorConfig = {
  toolbar: {
    items: [
      'exportPDF', 'exportWord', '|',
      'findAndReplace', 'selectAll', '|',
      'heading', '|',
      'bold', 'italic', 'strikethrough', 'underline', 'code', 'subscript',
      'superscript',
      'removeFormat', '|',
      'bulletedList', 'numberedList', 'todoList', '|',
      'outdent', 'indent', '|',
      'undo', 'redo',
      '-',
      'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
      'highlight', '|',
      'alignment', '|',
      'link', 'uploadImage', 'blockQuote', 'insertTable', 'mediaEmbed',
      'codeBlock', 'htmlEmbed',
      '|',
      'specialCharacters', 'horizontalLine', 'pageBreak', '|',
      'textPartLanguage', '|',
      'sourceEditing',
    ],
    shouldNotGroupWhenFull: true,
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
      { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
    ],
  },
  placeholder: 'Welcome to Editor',
  fontFamily: {
    options: [
      'default',
      'Arial, Helvetica, sans-serif',
      'Courier New, Courier, monospace',
      'Georgia, serif',
      'Lucida Sans Unicode, Lucida Grande, sans-serif',
      'Tahoma, Geneva, sans-serif',
      'Times New Roman, Times, serif',
      'Trebuchet MS, Helvetica, sans-serif',
      'Verdana, Geneva, sans-serif',
    ],
    supportAllValues: true,
  },
  fontSize: {
    options: [10, 12, 14, 'default', 18, 20, 22],
    supportAllValues: true,
  },
  htmlSupport: {
    allow: [{
      name: /.*/,
      attributes: true,
      classes: true,
      styles: true,
    }],
  },
  htmlEmbed: {
    showPreviews: true,
  },
  link: {
    decorators: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      toggleDownloadable: {
        mode: 'manual',
        label: 'Downloadable',
        attributes: { download: 'file' },
      },
    },
  },
  mention: {
    feeds: [{
      marker: '@',
      feed: [
        '@apple', '@bears', '@brownie', '@cake', '@candy', '@canes',
        '@chocolate', '@cookie', '@cotton', '@cream', '@cupcake', '@danish',
        '@donut', '@dragee', '@fruitcake', '@gingerbread', '@gummi', '@ice',
        '@jelly-o', '@liquorice', '@macaroon', '@marzipan', '@oat', '@pie',
        '@plum', '@pudding', '@sesame', '@snaps', '@souffle', '@sugar',
        '@sweet', '@topping', '@wafer',
      ],
      minimumCharacters: 1,
    }],
  },
  removePlugins: [
    'AIAssistant',
    'CKBox',
    'CKFinder',
    'EasyImage',
    'MultiLevelList',
    'RealTimeCollaborativeComments',
    'RealTimeCollaborativeTrackChanges',
    'RealTimeCollaborativeRevisionHistory',
    'PresenceList',
    'Comments',
    'TrackChanges',
    'TrackChangesData',
    'RevisionHistory',
    'Pagination',
    'WProofreader',
    'MathType',
    'SlashCommand',
    'Template',
    'DocumentOutline',
    'FormatPainter',
    'TableOfContents',
    'PasteFromOfficeEnhanced',
    'CaseChange',
  ],
};

const loadCkEditor = () => {
  if (window.CKEDITOR) return Promise.resolve();
  ckEditorLoader ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${ckEditorScriptUrl}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CKEditor failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = ckEditorScriptUrl;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('CKEditor failed to load.')), { once: true });
    document.head.appendChild(script);
  });
  return ckEditorLoader;
};

const readOnlyLockId = 'ckeditor-field-disabled';

export const CkEditorField = ({
  value,
  onChange,
  className = '',
  placeholder,
  ariaLabel,
  disabled = false,
  label,
  defaultOpen,
}: {
  value: string | null;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  label?: ReactNode;
  defaultOpen?: boolean;
}) => {
  const collapsible = Boolean(label);
  const [open, setOpen] = useState(defaultOpen ?? !collapsible);
  const [everOpened, setEverOpened] = useState(defaultOpen ?? !collapsible);
  const sourceRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<CkEditorInstance | null>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value ?? '');
  const disabledRef = useRef(disabled);
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    setOpen((current) => {
      const next = !current;
      if (next) setEverOpened(true);
      return next;
    });
  };

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    disabledRef.current = disabled;
    const editor = editorRef.current;
    if (!editor) return;
    if (disabled) editor.enableReadOnlyMode(readOnlyLockId);
    else editor.disableReadOnlyMode(readOnlyLockId);
  }, [disabled]);

  useEffect(() => {
    valueRef.current = value ?? '';
    const editor = editorRef.current;
    if (editor && editor.getData() !== valueRef.current) editor.setData(valueRef.current);
  }, [value]);

  useEffect(() => {
    if (!everOpened) return undefined;
    let cancelled = false;
    const source = sourceRef.current;
    if (!source) return undefined;
    source.value = valueRef.current;

    loadCkEditor()
      .then(async () => {
        if (cancelled || !window.CKEDITOR || editorRef.current) return;
        const editor = await window.CKEDITOR.ClassicEditor.create(source, {
          ...ckEditorConfig,
          ...(placeholder ? { placeholder } : {}),
        });
        if (cancelled) {
          await editor.destroy();
          return;
        }
        editorRef.current = editor;
        if (editor.getData() !== valueRef.current) editor.setData(valueRef.current);
        if (disabledRef.current) editor.enableReadOnlyMode(readOnlyLockId);
        editor.model.document.on('change:data', () => {
          const next = editor.getData();
          valueRef.current = next;
          onChangeRef.current(next);
        });
      })
      .catch(() => {
        if (!cancelled) setError('CKEditor could not be loaded.');
      });

    return () => {
      cancelled = true;
      const editor = editorRef.current;
      editorRef.current = null;
      if (editor) void editor.destroy();
    };
  }, [everOpened, placeholder]);

  return (
    <div className={`legacy-ckeditor-field ${className}`.trim()}>
      {collapsible ? (
        <div
          role="button"
          tabIndex={0}
          className="legacy-ckeditor-toggle"
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggle();
          }}
        >
          <IconChevron className="legacy-ckeditor-chevron" />
          <span>{label}</span>
        </div>
      ) : null}
      <div className="legacy-ckeditor-body" hidden={!open}>
        <textarea
          ref={sourceRef}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : 'CKEditor')}
          className="text"
          defaultValue={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      </div>
      {error ? <small>{error}</small> : null}
    </div>
  );
};
